import type { CSSProperties } from 'react';
import { useState } from 'react';

import type { TriageVenue } from '../../../../api/regions';

interface Props {
  venue: TriageVenue;
  regionOptions: Array<{ id: string; label: string }>;
  pending: boolean;
  onAccept: (regionId: string) => void;
}

const rowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  padding: '10px 12px',
  borderRadius: 10,
  border: '1px solid var(--nb-border)',
  background: 'var(--nb-bg)',
};
const selectStyle: CSSProperties = {
  height: 30,
  borderRadius: 8,
  border: '1px solid var(--nb-border)',
  background: 'var(--nb-bg)',
  font: 'inherit',
  fontSize: 12,
  color: 'var(--nb-ink)',
  padding: '0 8px',
  maxWidth: 190,
};

/** One unassigned venue: accept the suggested region or pick one (or geocode). */
export function TriageRow({ venue, regionOptions, pending, onAccept }: Props) {
  const [choice, setChoice] = useState(venue.suggestedRegionId ?? '');

  return (
    <div style={rowStyle}>
      <div style={{ width: 200, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--nb-ink)' }}>{venue.name}</div>
        <div style={{ fontSize: 11, color: 'var(--nb-subtle)' }}>
          {venue.city}, {venue.state}
        </div>
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        {venue.needsCoordinates ? (
          <span
            style={{
              fontSize: 12,
              fontWeight: 600,
              color: 'var(--nb-warn)',
              background: 'var(--nb-warn-soft)',
              padding: '4px 9px',
              borderRadius: 7,
            }}
          >
            Needs coordinates — geocode in venue settings
          </span>
        ) : (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <select
              aria-label={`Region for ${venue.name}`}
              value={choice}
              onChange={(e) => setChoice(e.target.value)}
              style={selectStyle}
            >
              <option value="">Choose a region…</option>
              {regionOptions.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.label}
                </option>
              ))}
            </select>
            {venue.suggestedRegionName && (
              <span style={{ fontSize: 10.5, color: 'var(--nb-info)' }}>suggested</span>
            )}
          </span>
        )}
      </div>

      {!venue.needsCoordinates && (
        <button
          className="nb-btn nb-btn-primary"
          disabled={pending || !choice}
          onClick={() => onAccept(choice)}
          style={{ fontSize: 11.5 }}
        >
          Accept
        </button>
      )}
    </div>
  );
}
